/**
 * read-only-access.tsx
 *
 * Whether the property on screen may only be read, and why.
 *
 * A viewer — read-only access to one property, granted from the access panel —
 * sees the property's prices and analytics and changes nothing. The server is
 * what holds them to that (`optivo-service/src/middleware/readOnlyAccess.ts`
 * refuses every write and every strategy read); this context is how a screen
 * knows to leave out the controls that would only fail, without each page
 * threading a flag through the components between the shell and the button.
 *
 * The same grant held by an administrator reads as `staff`: an administrator
 * adds themselves to a property read-only to review it, sees what a member
 * sees — the strategy, the settings, the full price explanation — and changes
 * nothing either. So a screen that leaves something out for a viewer asks
 * which kind of read-only it is: `useViewerAccess()` for what a viewer alone
 * is not shown, `useReadOnlyAccess()` for the controls nobody read-only may
 * use.
 *
 * A viewer's grant is of one of two kinds. `management` reads the prices and
 * the analytics; `front_desk` — the hotel's receptionists — reads the prices,
 * the reservations and a price's short explanation, and no analytics.
 * `useFrontDeskAccess()` asks for the second. The front desk is still a
 * viewer, so `useViewerAccess()` stays true for it and every viewer cut holds.
 *
 * Outside a provider the answer is `false`, so a component rendered on its own
 * — in a test, in the wizard — behaves exactly as it did before viewers
 * existed.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ViewerKind } from "../../types/config";

/** Why the screen may only be read: a viewer's grant, or the same grant held by an administrator. */
export type ReadOnlyReason = "viewer" | "staff";

/**
 * What the context holds while the property may only be read: why, and for a
 * viewer the kind of their grant. One value, so a nested `readOnly={false}`
 * provider (the account page's own sections) clears both at once.
 */
interface ReadOnlyAccess {
    reason: ReadOnlyReason;
    /** The kind of a viewer's grant; null for staff, who has none. */
    viewerKind: ViewerKind | null;
}

const ReadOnlyAccessContext = createContext<ReadOnlyAccess | null>(null);

interface ReadOnlyAccessProviderProps {
    readOnly: boolean;
    /** Why — a viewer's grant unless said otherwise. */
    reason?: ReadOnlyReason | null;
    /**
     * The kind of a viewer's grant. Nothing at all reads as `management`, the
     * kind every grant was before the front desk existed, so a provider that
     * names no kind keeps the meaning it always had. Staff has no kind.
     */
    viewerKind?: ViewerKind | null;
    children: ReactNode;
}

export function ReadOnlyAccessProvider({ readOnly, reason, viewerKind, children }: ReadOnlyAccessProviderProps) {
    const resolvedReason: ReadOnlyReason | null = readOnly ? (reason ?? "viewer") : null;
    const resolvedKind: ViewerKind | null = resolvedReason === "viewer" ? (viewerKind ?? "management") : null;
    const value = useMemo<ReadOnlyAccess | null>(
        () => (resolvedReason === null ? null : { reason: resolvedReason, viewerKind: resolvedKind }),
        [resolvedReason, resolvedKind],
    );
    return <ReadOnlyAccessContext.Provider value={value}>{children}</ReadOnlyAccessContext.Provider>;
}

/** True when the open property may only be read — by a viewer or by staff. */
export function useReadOnlyAccess(): boolean {
    return useContext(ReadOnlyAccessContext) !== null;
}

/**
 * True for a viewer, who is shown a property's prices and analytics and not its
 * strategy. The front desk is a viewer too, so everything left out for a viewer
 * is left out for the front desk as well.
 */
export function useViewerAccess(): boolean {
    return useContext(ReadOnlyAccessContext)?.reason === "viewer";
}

/** True for staff: an administrator holding a read-only grant, who reads every screen a member reads and changes nothing. */
export function useStaffAccess(): boolean {
    return useContext(ReadOnlyAccessContext)?.reason === "staff";
}

/**
 * True for the front desk: a viewer whose grant is the hotel's receptionists',
 * who reads the prices, the reservations and a price's short explanation, and
 * nothing that states what the property earns. It only ever narrows what
 * `useViewerAccess()` already leaves out.
 */
export function useFrontDeskAccess(): boolean {
    const access = useContext(ReadOnlyAccessContext);
    return access?.reason === "viewer" && access.viewerKind === "front_desk";
}
